import { lazy, Suspense, type ComponentType, type LazyExoticComponent } from "react";
import { createBrowserRouter, Navigate, Outlet, useLocation, type RouteObject } from "react-router";
import { useAuth } from "@ncp/api-client";
import { RootLayout, WorkspaceLayout } from "./layouts";
import { ForbiddenPage } from "../pages/ForbiddenPage";
import { SHELL } from "./flags";
import { frozenRoutes, RequirePermission } from "./routes.legacy";
import { BARE_ROUTES, SESSION_ROUTES, routePermissions, type RouteDef } from "./routes";
import { WorkspaceEntry } from "./WorkspaceEntry";
import { useWorkspace } from "./workspace-context";
import { RootErrorElement, RouteErrorElement } from "../econic/shell/a11y";
import { withLocales } from "./locales-ready";
import { WaitlistGate } from "../econic/engagement/gate";

export { SHELL, type ShellFlag } from "./flags";
export { RequirePermission };

// ------------------------------------------------------------------ ECONIC: routes from routes.tsx (shell-ia §14)

const screens = new Map<RouteDef["lazy"], LazyExoticComponent<ComponentType>>();
/** One lazy component per screen loader, so routes of the same screen (`/tasks`, `/tasks/:key`) do not remount it. */
function screenOf(def: RouteDef): LazyExoticComponent<ComponentType> {
  let screen = screens.get(def.lazy);
  if (!screen) {
    screen = lazy(withLocales(def.lazy));
    screens.set(def.lazy, screen);
  }
  return screen;
}

function Screen({ def }: { def: RouteDef }) {
  const Component = screenOf(def);
  return (
    <Suspense fallback={<ScreenSkeleton layout={def.layout} />}>
      <Component />
    </Suspense>
  );
}

/** While a screen's chunk loads: the header and card shapes of the page, never a spinner (DESIGN-SYSTEM §10.16). */
function ScreenSkeleton({ layout }: { layout: RouteDef["layout"] }) {
  if (layout === "bare" || layout === "full") return null;
  return (
    <div aria-busy="true" className="flex flex-col gap-3" data-econic-skeleton="">
      <div className="ec-skel" style={{ height: layout === "hero" ? 240 : 92, borderRadius: 8 }} />
      <div className="grid grid-cols-12 gap-3">
        <div className="ec-skel col-span-12 md:col-span-6" style={{ height: 180, borderRadius: 8 }} />
        <div className="ec-skel col-span-12 md:col-span-6" style={{ height: 180, borderRadius: 8 }} />
      </div>
    </div>
  );
}

/** A signed-in screen: the route's permission (any one of a list) from the session or the catalog, then the lazy screen. */
function SessionScreen({ def }: { def: RouteDef }) {
  const { can, loading } = useWorkspace();
  const required = routePermissions(def);
  // The content-blind DT Administrator lands on the Trust Centre, not on a Today they may not open (SPEC §3.1).
  if (def.id === "today" && !loading && !can("assistant.use") && can("trust.read")) return <Navigate to="/trust" replace />;
  if (required.length && !required.some((p) => can(p))) return loading ? null : <ForbiddenPage permission={required.join(" · ")} />;
  return <Screen def={def} />;
}

/** Every route except the landing and sign-in needs a session; the screen's own permission is checked by `SessionScreen`. */
function RequireSession() {
  const { isAuthenticated, user } = useAuth();
  const { pathname, search, hash } = useLocation();
  if (!isAuthenticated || !user) return <Navigate to="/login" replace state={{ from: `${pathname}${search}${hash}` }} />;
  // An account on the ECONIC waitlist sees the waiting room instead (28 Oct 2026).
  return <WaitlistGate><Outlet /></WaitlistGate>;
}

function econicRoutes(): RouteObject[] {
  const notFound = SESSION_ROUTES.find((d) => d.path === "*");
  // A route that throws renders "This screen could not load" in the shell's content column (each signed-in route has its own error
  // element, so the strip, the toolbar and the agent stay and every shell provider is mounted); an error above the frame gets the same
  // card on a bare window (RootErrorElement uses no shell hook).
  const inShell = <RouteErrorElement />;
  return [
    {
      element: <RootLayout />,
      errorElement: <RootErrorElement />,
      children: [
        ...BARE_ROUTES.map((def) => ({ path: def.path, element: <Screen def={def} /> })),
        {
          element: <RequireSession />,
          children: [
            {
              // WorkspaceLayout mounts EconicShell under VITE_SHELL=econic (app/layouts.tsx).
              element: <WorkspaceLayout />,
              children: [
                ...SESSION_ROUTES.filter((d) => d !== notFound).map((def) => ({ path: def.path, element: <SessionScreen def={def} />, errorElement: inShell })),
                { path: "/w/:workspaceKey/*", element: <WorkspaceEntry />, errorElement: inShell },
                notFound ? { path: "*", element: <SessionScreen def={notFound} />, errorElement: inShell } : { path: "*", element: <Navigate to="/" replace /> },
              ],
            },
          ],
        },
      ],
    },
  ];
}

export const router = createBrowserRouter(SHELL === "econic" ? econicRoutes() : frozenRoutes(SHELL));
